import { cn } from '@/utils/cn';
import { Typography } from '../Typography';
import type { AvatarSize } from '../Avatar';
import { placeholderUrlForId } from './userAvatarUtils';

// ─── Size maps ────────────────────────────────────────────────────────────────

const sizeClassMap: Record<AvatarSize, string> = {
  small: 'size-6',
  regular: 'size-7',
  medium: 'size-8',
  large: 'size-10',
};

const initialsTypographyMap: Record<
  AvatarSize,
  { variant: 'body'; size: 'xs' | 'small' | 'medium' | 'large' }
> = {
  small: { variant: 'body', size: 'xs' },
  regular: { variant: 'body', size: 'small' },
  medium: { variant: 'body', size: 'medium' },
  large: { variant: 'body', size: 'large' },
};

// ─── Types ────────────────────────────────────────────────────────────────────

/**
 * Props for the UserAvatar component.
 *
 * `className` is intentionally omitted — UserAvatar is a sealed design system
 * component. All visual state is controlled through named props.
 */
export interface UserAvatarProps {
  /**
   * The user's numeric ID. Used to derive a consistent CDN placeholder image
   * when `src` is not provided.
   */
  userId: number;
  /**
   * Override image URL (e.g. a user-uploaded profile photo). When provided
   * this takes precedence over the placeholder derived from `userId`.
   */
  src?: string;
  /**
   * One or two characters displayed on the avatar background when no `src`
   * is provided. Typically the user's initials (e.g. `"JM"`).
   * When omitted the component falls back to the CDN placeholder image.
   */
  initials?: string;
  /**
   * Controls the rendered dimensions. Forwarded directly to the underlying
   * avatar element.
   *
   * | Size     | px  |
   * |----------|-----|
   * | small    | 32  |
   * | regular  | 40  |
   * | medium   | 48  |
   * | large    | 64  |
   *
   * @default "regular"
   */
  size?: AvatarSize;
  /**
   * When `true` the avatar is rendered as a full circle.
   * When `false` a small border-radius is used instead.
   *
   * @default true
   */
  rounded?: boolean;
}

// ─── Component ────────────────────────────────────────────────────────────────

/**
 * Primer UserAvatar component.
 *
 * A user-specific avatar that renders in this priority order:
 * 1. Uploaded photo (`src` prop)
 * 2. Initials on the brand background (`initials` prop)
 * 3. CDN placeholder image derived from `userId`
 *
 * The placeholder URL is computed as:
 * ```
 * profiledefault-{userId % 10}.png
 * ```
 * giving 10 visually distinct default images that are stable per user.
 *
 * ### Sealed component
 * `className` is not exposed. If a new visual state is needed, add a prop.
 *
 * @example
 * // CDN placeholder (no uploaded photo, no initials)
 * <UserAvatar userId={42} size="regular" />
 *
 * @example
 * // Initials shown in place of an image
 * <UserAvatar userId={42} initials="JM" size="large" />
 *
 * @example
 * // Uploaded photo takes full priority
 * <UserAvatar userId={42} src={user.profileImageUrl} size="large" />
 */
export function UserAvatar({
  userId,
  src,
  initials,
  size = 'regular',
  rounded = true,
}: UserAvatarProps) {
  const sizeClass = sizeClassMap[size];
  const shapeClass = rounded ? 'rounded-full' : 'rounded-small';

  const containerClass = cn(
    'relative flex items-center justify-center overflow-hidden bg-media flex-shrink-0',
    sizeClass,
    shapeClass
  );

  // Priority 1: uploaded photo
  if (src) {
    return (
      <div className={containerClass}>
        <img src={src} alt="" className="object-cover w-full h-full" />
      </div>
    );
  }

  // Priority 2: initials
  if (initials) {
    const { variant, size: typographySize } = initialsTypographyMap[size];
    return (
      <div className={containerClass}>
        <Typography variant={variant} size={typographySize} color="white" as="span">
          {initials.slice(0, 2)}
        </Typography>
      </div>
    );
  }

  // Priority 3: CDN placeholder image
  const placeholderSrc = placeholderUrlForId(userId);
  return (
    <div className={containerClass}>
      <img src={placeholderSrc} alt="" className="object-cover w-full h-full" />
    </div>
  );
}

export default UserAvatar;
